import React, { useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, Popover } from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faSearch, faFileUpload, faChevronDown } from '@fortawesome/free-solid-svg-icons';

import { SanityCounts } from '../api/workItems';
import { AreaOption, ViewKey, ViewOption, StateOption } from './WorkItemsView.constants';

/** How many area pills to show before collapsing into "More ▾" */
const MAX_VISIBLE_PILLS = 5;

type WorkItemsHeaderProps = {
    areas: AreaOption[];
    /** Resolved area (null while waiting for bootstrap when URL has no `area` param). */
    areaFilter: string | null;
    currentView: ViewKey;
    stateFilter: string;
    search: string;
    needsActionOnly: boolean;
    sanityCounts: SanityCounts | null;
    views: ViewOption[];
    states: StateOption[];
    onViewChange: (view: ViewKey) => void;
    onAreaChange: (areaId: string) => void;
    onSearchChange: (value: string) => void;
    onToggleNeedsAction: () => void;
    onImportClick: () => void;
    onShortcutsClick: () => void;
    onStateChange: (stateId: string) => void;
};

const WorkItemsHeader: React.FC<WorkItemsHeaderProps> = ({
    areas,
    areaFilter,
    currentView,
    stateFilter,
    search,
    needsActionOnly,
    sanityCounts,
    views,
    states,
    onViewChange,
    onAreaChange,
    onSearchChange,
    onToggleNeedsAction,
    onImportClick,
    onShortcutsClick,
    onStateChange,
}) => {
    // Dynamic title from the active view tab
    const activeViewLabel = views.find((v) => v.id === currentView)?.label || 'Workbench';

    // Split areas into visible pills + overflow dropdown
    const { visibleAreas, overflowAreas } = useMemo(() => {
        // If an active filter is in the overflow, promote it to visible
        const activeInOverflow = areas.length > MAX_VISIBLE_PILLS
            && areas.findIndex((a) => a.id === areaFilter) >= MAX_VISIBLE_PILLS;

        if (areas.length <= MAX_VISIBLE_PILLS) {
            return { visibleAreas: areas, overflowAreas: [] as AreaOption[] };
        }

        if (activeInOverflow) {
            // Swap the active overflow item with the last visible item
            const activeIdx = areas.findIndex((a) => a.id === areaFilter);
            const reordered = [...areas];
            const [active] = reordered.splice(activeIdx, 1);
            reordered.splice(MAX_VISIBLE_PILLS - 1, 0, active);
            return {
                visibleAreas: reordered.slice(0, MAX_VISIBLE_PILLS),
                overflowAreas: reordered.slice(MAX_VISIBLE_PILLS),
            };
        }

        return {
            visibleAreas: areas.slice(0, MAX_VISIBLE_PILLS),
            overflowAreas: areas.slice(MAX_VISIBLE_PILLS),
        };
    }, [areas, areaFilter]);

    // "More" dropdown state
    const [moreAnchor, setMoreAnchor] = useState<HTMLButtonElement | null>(null);
    const moreOpen = Boolean(moreAnchor);
    const isActiveInOverflow = overflowAreas.some((a) => a.id === areaFilter);

    const renderAreaPill = (a: AreaOption) => {
        const count = a.id === 'all'
            ? sanityCounts?.open ?? 0
            : sanityCounts?.byDomain?.[a.id] ?? 0;
        return (
            <button
                key={a.id}
                onClick={() => { onAreaChange(a.id); setMoreAnchor(null); }}
                aria-pressed={areaFilter === a.id}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-all ${areaFilter === a.id
                    ? 'bg-indigo-500/20 border-indigo-500/40 text-chip-indigo'
                    : 'border-theme text-theme-secondary hover:text-theme hover:bg-theme-card-hover'
                    }`}
                style={{ borderColor: areaFilter !== a.id ? 'var(--color-border)' : undefined }}
            >
                {a.label}{count > 0 && <span className="ml-1 opacity-60">({count})</span>}
            </button>
        );
    };

    return (
        <Box className="glass-card p-4 sm:p-6 bg-theme-secondary border border-theme rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden relative">
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-indigo-500/10 blur-[100px] rounded-full pointer-events-none"></div>

            {/* ── Row 1: Title + View tabs + Search + actions ── */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 relative z-10">
                <h1 className="text-2xl font-bold text-theme tracking-tight shrink-0">
                    {activeViewLabel}
                </h1>

                {/* View tabs */}
                <div className="flex bg-theme-card p-1 rounded-2xl border border-theme backdrop-blur-md shrink-0 overflow-x-auto">
                    {views.map((v) => (
                        <button
                            key={v.id}
                            onClick={() => onViewChange(v.id)}
                            aria-pressed={currentView === v.id}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap ${currentView === v.id
                                ? 'bg-indigo-500 text-white shadow-md'
                                : 'text-theme-secondary hover:text-theme hover:bg-theme-card-hover'
                                }`}
                        >
                            <FontAwesomeIcon icon={v.icon} className="text-[10px]" />
                            {v.label}
                        </button>
                    ))}
                </div>

                {/* Search */}
                <div className="relative group flex-1 min-w-[200px]">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <FontAwesomeIcon icon={faSearch} className="text-theme-muted group-focus-within:text-chip-indigo transition-colors text-xs" />
                    </div>
                    <input
                        type="text"
                        data-testid="WB_SEARCH"
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Search items..."
                        className="w-full rounded-xl py-2 pl-9 pr-3 text-sm transition-all"
                        style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                    />
                </div>

                {/* Toolbar icons */}
                <div className="flex items-center gap-2 shrink-0">
                    <Tooltip title="Import File">
                        <IconButton
                            onClick={onImportClick}
                            size="small"
                            sx={{
                                color: 'var(--color-text-muted)',
                                border: '1px solid var(--color-border)',
                                borderRadius: '12px',
                                '&:hover': { color: '#10b981', borderColor: 'rgba(16,185,129,0.4)', bgcolor: 'rgba(16,185,129,0.06)' },
                            }}
                        >
                            <FontAwesomeIcon icon={faFileUpload} size="xs" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Keyboard Shortcuts">
                        <IconButton
                            onClick={onShortcutsClick}
                            size="small"
                            sx={{
                                color: 'var(--color-text-muted)',
                                border: '1px solid var(--color-border)',
                                borderRadius: '12px',
                                '&:hover': { color: 'var(--color-accent)', borderColor: 'rgba(99,102,241,0.4)', bgcolor: 'rgba(99,102,241,0.06)' },
                            }}
                        >
                            <KeyboardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>

            {/* ── Row 2: Area pills (with overflow) + State pills + Needs Action ── */}
            <div className="mt-4 flex min-w-0 flex-wrap items-center gap-3 relative z-10">
                {/* Area pills. On a phone this is the horizontal scrollport: the card clips
                    overflow, so without min-w-0 the row grows and a left drag cannot move it.
                    Overflow-x stays on at every width (landscape phones included). Vertical
                    page pans over the row still work. Below sm the row keeps its own full-width
                    line; flex-1 there would share the line with the state pills and collapse it. */}
                <div
                    data-testid="WB_AREA_CHIPS"
                    className="flex w-full min-w-0 items-center gap-1.5 overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:w-auto sm:flex-1"
                >
                    {visibleAreas.map(renderAreaPill)}

                    {/* "More" dropdown for overflow domains */}
                    {overflowAreas.length > 0 && (
                        <>
                            <button
                                onClick={(e) => setMoreAnchor(e.currentTarget)}
                                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-all flex items-center gap-1 ${isActiveInOverflow
                                    ? 'bg-indigo-500/20 border-indigo-500/40 text-chip-indigo'
                                    : 'border-theme text-theme-secondary hover:text-theme hover:bg-theme-card-hover'
                                    }`}
                                style={{ borderColor: isActiveInOverflow ? undefined : 'var(--color-border)' }}
                            >
                                +{overflowAreas.length} more
                                <FontAwesomeIcon icon={faChevronDown} className="text-[8px]" />
                            </button>
                            <Popover
                                open={moreOpen}
                                anchorEl={moreAnchor}
                                onClose={() => setMoreAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            mt: 1,
                                            bgcolor: 'var(--color-bg-card)',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: '16px',
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                                            p: 1,
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '6px',
                                            maxWidth: 360,
                                        },
                                    },
                                }}
                            >
                                {overflowAreas.map(renderAreaPill)}
                            </Popover>
                        </>
                    )}
                </div>

                {/* Visual separator */}
                <div className="hidden sm:block h-5 w-px" style={{ backgroundColor: 'var(--color-border)' }} />

                {/* State pills */}
                <div className="flex items-center gap-1 p-0.5 rounded-xl" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
                    {states.map((s) => (
                        <button
                            key={s.id}
                            onClick={() => onStateChange(s.id)}
                            aria-pressed={stateFilter === s.id}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${stateFilter === s.id
                                ? 'bg-indigo-500/20 text-chip-indigo'
                                : 'text-theme-muted hover:text-theme-secondary'
                                }`}
                        >
                            <FontAwesomeIcon icon={s.icon} className="text-[9px]" />
                            {s.label}
                        </button>
                    ))}
                </div>

                {/* Visual separator */}
                <div className="hidden sm:block h-5 w-px" style={{ backgroundColor: 'var(--color-border)' }} />

                {/* Needs Action toggle */}
                <button
                    onClick={onToggleNeedsAction}
                    aria-pressed={needsActionOnly}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-wider border transition-all ${needsActionOnly
                        ? 'bg-amber-500/10 border-amber-500/30 text-chip-warning'
                        : 'text-theme-secondary border-theme hover:bg-theme-card-hover'
                        }`}
                    style={{ borderColor: needsActionOnly ? undefined : 'var(--color-border)' }}
                >
                    <FontAwesomeIcon icon={faExclamationCircle} className={needsActionOnly ? 'animate-pulse' : ''} />
                    Needs Action
                </button>
            </div>
        </Box>
    );
};

export default WorkItemsHeader;
